import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/dialog/dialog.js";
import "@material/web/icon/icon.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";
import type { Product } from "../lib/types";

// Mirrors internal/modules/recall/domain.go. Action is what the holder should
// DO — deliberately small and universal; Class carries the regulator's own
// wording verbatim.
type RecallAction = "stop_using" | "return" | "contact" | "none_needed";

interface RecallNotice {
  id: string;
  orgId: string;
  productId: string;
  /** EMPTY MEANS THE WHOLE PRODUCT — a recall defaults to maximal reach. */
  batches: string[] | null;
  /** Empty means EVERYWHERE. */
  markets: string[] | null;
  action: RecallAction;
  headline: string;
  detail?: string;
  class?: string;
  reference?: string;
  contactUrl?: string;
  contactPhone?: string;
  issuedAt: string;
  liftedAt?: string;
}

const ACTION_LABELS: Record<RecallAction, string> = {
  stop_using: "Stop using",
  return: "Return",
  contact: "Contact for next steps",
  none_needed: "None needed (withdrawal)",
};

// The safety cases, which dominate the tap page. Mirrors Action.Severe() —
// the server also requires a contact for these.
function severe(a: RecallAction): boolean {
  return a === "stop_using" || a === "return";
}

// "DE, FR, batch A1, A2" → trimmed, de-duplicated list; undefined when empty
// so the JSON body omits the field (empty = all, per the domain).
function splitList(raw: string): string[] | undefined {
  const items = [...new Set(raw.split(",").map((s) => s.trim()).filter(Boolean))];
  return items.length > 0 ? items : undefined;
}

// Recall authoring for producers. Issuing publishes immediately to every
// holder at tap time; lifting is public too, and lifted notices stay in the
// history — there is deliberately no delete, on the API or the collection.
@customElement("recall-page")
export class RecallPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      form.dialog-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-top: 8px;
        min-width: min(440px, 72vw);
      }
      .recall-banner {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        background: var(--md-sys-color-error-container);
        color: var(--md-sys-color-on-error-container);
        border-radius: 16px;
        padding: 16px 20px;
      }
      .recall-banner md-icon {
        --md-icon-size: 22px;
        flex-shrink: 0;
        margin-top: 1px;
      }
      .recall-banner b {
        display: block;
        margin-bottom: 4px;
      }
      .recall-banner p {
        margin: 0;
        font-size: 0.8125rem;
        line-height: 1.45;
      }
      .row-actions {
        display: flex;
        gap: 4px;
        justify-content: flex-end;
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private notices: RecallNotice[] = [];
  @state() private products: Product[] = [];
  @state() private loading = true;
  @state() private error = "";

  // Issue dialog.
  @state() private fProduct = "";
  @state() private fAction: RecallAction = "stop_using";
  @state() private fHeadline = "";
  @state() private fDetail = "";
  @state() private fClass = "";
  @state() private fReference = "";
  @state() private fContactUrl = "";
  @state() private fContactPhone = "";
  @state() private fBatches = "";
  @state() private fMarkets = "";
  @state() private issuing = false;
  @state() private issueError = "";

  // Lift confirmation.
  @state() private liftTarget?: RecallNotice;
  @state() private lifting = false;

  @query("#issue-dialog") private issueDialog?: MdDialog;
  @query("#lift-dialog") private liftDialog?: MdDialog;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  // Who may author here. Editors may issue, not just admins — a recall is a
  // race against distribution, and the person who knows first is usually not
  // the person with the admin seat. The server enforces the same gate (and
  // rejects everyone else's GET too), so there is nothing to show without it.
  private get canIssue(): boolean {
    return (
      this.auth.hasRole("editor") ||
      this.auth.hasRole("org_admin") ||
      this.auth.hasRole("platform_operator")
    );
  }

  private async load(): Promise<void> {
    if (!this.canIssue) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = "";
    try {
      // Two independent reads, issued together. Products come from the same
      // catalog endpoint as the products page; recall notices only carry a
      // productId, so names are resolved from that list.
      const [notices, products] = await Promise.all([
        api.get<RecallNotice[] | null>("/recall/notices"),
        api.get<Product[]>("/catalog/products"),
      ]);
      this.notices = notices ?? [];
      this.products = products;
    } catch (err) {
      this.error = errorMessage(err, "Failed to load recall notices.");
    } finally {
      this.loading = false;
    }
  }

  private get activeNotices(): RecallNotice[] {
    return this.notices
      .filter((n) => !n.liftedAt)
      .sort((a, b) => +new Date(b.issuedAt) - +new Date(a.issuedAt));
  }

  private get liftedNotices(): RecallNotice[] {
    return this.notices
      .filter((n) => n.liftedAt)
      .sort((a, b) => +new Date(b.liftedAt!) - +new Date(a.liftedAt!));
  }

  private productName(productID: string): string {
    return this.products.find((p) => p.ID === productID)?.Name || productID;
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleDateString();
  }

  private actionBadge(a: RecallAction) {
    if (severe(a)) {
      return html`<span class="badge badge--bad"
        >${icon("flag")} ${ACTION_LABELS[a]}</span
      >`;
    }
    if (a === "none_needed") {
      return html`<span class="badge badge--neutral"
        >${icon("check_circle")} ${ACTION_LABELS[a]}</span
      >`;
    }
    return html`<span class="badge badge--neutral"
      >${icon("campaign")} ${ACTION_LABELS[a]}</span
    >`;
  }

  // Empty lists mean maximal reach (the whole product, everywhere) — the
  // domain's deliberate default, said out loud rather than left blank.
  private scopeCell(n: RecallNotice) {
    const batches = n.batches?.length ? n.batches.join(", ") : "All batches";
    const markets = n.markets?.length ? n.markets.join(", ") : "All markets";
    return html`${batches}<small>${markets}</small>`;
  }

  private openIssue(): void {
    this.fProduct = this.products[0]?.ID ?? "";
    this.fAction = "stop_using";
    this.fHeadline = this.fDetail = this.fClass = this.fReference = "";
    this.fContactUrl = this.fContactPhone = "";
    this.fBatches = this.fMarkets = "";
    this.issueError = "";
    this.issueDialog?.show();
  }

  private openLift(n: RecallNotice): void {
    this.liftTarget = n;
    this.liftDialog?.show();
  }

  private async handleIssue(e: Event): Promise<void> {
    e.preventDefault();
    this.issueError = "";
    if (!this.fProduct) {
      this.issueError = "A product is required.";
      return;
    }
    if (!this.fHeadline.trim()) {
      this.issueError =
        "A headline is required — it is the sentence somebody reads if they read nothing else.";
      return;
    }
    // Mirrors the server: a "stop using" or "return" notice with no way to
    // reach anybody leaves a frightened person with no next step.
    if (
      severe(this.fAction) &&
      !this.fContactUrl.trim() &&
      !this.fContactPhone.trim()
    ) {
      this.issueError = `A "${ACTION_LABELS[this.fAction].toLowerCase()}" notice needs a contact URL or phone number.`;
      return;
    }
    this.issuing = true;
    try {
      // Org comes from auth on the server, never from the body.
      const notice = await api.post<RecallNotice>("/recall/notices", {
        productId: this.fProduct,
        batches: splitList(this.fBatches),
        markets: splitList(this.fMarkets),
        action: this.fAction,
        headline: this.fHeadline.trim(),
        detail: this.fDetail.trim(),
        class: this.fClass.trim(),
        reference: this.fReference.trim(),
        contactUrl: this.fContactUrl.trim(),
        contactPhone: this.fContactPhone.trim(),
      });
      this.notices = [notice, ...this.notices];
      this.issueDialog?.close();
      toast(
        `Recall issued — now live for every holder of ${this.productName(notice.productId)}.`,
      );
    } catch (err) {
      this.issueError = errorMessage(err, "Failed to issue the recall.");
    } finally {
      this.issuing = false;
    }
  }

  private async handleLift(): Promise<void> {
    if (!this.liftTarget) return;
    this.lifting = true;
    try {
      const updated = await api.post<RecallNotice>(
        `/recall/notices/${this.liftTarget.id}/lift`,
      );
      this.notices = this.notices.map((n) =>
        n.id === updated.id ? updated : n,
      );
      this.liftDialog?.close();
      toast("Recall lifted. The notice stays in your history.");
    } catch (err) {
      toast(errorMessage(err, "Failed to lift the recall."), true);
    } finally {
      this.lifting = false;
    }
  }

  private renderActive() {
    const active = this.activeNotices;
    if (active.length === 0) {
      return html`<div class="card empty">
        No recalls in force. If you ever need one, it reaches every holder the
        moment you issue it.
      </div>`;
    }
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Active recalls <small>· ${active.length}</small></h2>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Headline</th>
              <th>Action</th>
              <th>Issued</th>
              <th>Scope</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${active.map(
              (n) => html`
                <tr>
                  <td>
                    <b>${this.productName(n.productId)}</b>
                    ${n.reference ? html`<small>${n.reference}</small>` : null}
                  </td>
                  <td>
                    ${n.headline}
                    ${n.class ? html`<small>${n.class}</small>` : null}
                  </td>
                  <td>${this.actionBadge(n.action)}</td>
                  <td>${this.formatDate(n.issuedAt)}</td>
                  <td>${this.scopeCell(n)}</td>
                  <td>
                    <div class="row-actions">
                      <md-text-button @click=${() => this.openLift(n)}>
                        Lift
                      </md-text-button>
                    </div>
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  private renderHistory() {
    const lifted = this.liftedNotices;
    if (lifted.length === 0) return null;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>History <small>· ${lifted.length} lifted</small></h2>
          <p class="muted">
            Lifted notices are kept, not deleted — "was this ever recalled, and
            when?" must stay answerable.
          </p>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Headline</th>
              <th>Action</th>
              <th>Issued</th>
              <th>Lifted</th>
            </tr>
          </thead>
          <tbody>
            ${lifted.map(
              (n) => html`
                <tr>
                  <td>
                    <b>${this.productName(n.productId)}</b>
                    ${n.reference ? html`<small>${n.reference}</small>` : null}
                  </td>
                  <td>${n.headline}</td>
                  <td>${this.actionBadge(n.action)}</td>
                  <td>${this.formatDate(n.issuedAt)}</td>
                  <td>${this.formatDate(n.liftedAt ?? "")}</td>
                </tr>
              `,
            )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  render() {
    return html`
      <app-shell active="/recall">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Product recalls</h1>
              <p>
                Safety notices attached to your passports. Anyone who taps a
                covered item sees the notice immediately.
              </p>
            </div>
            ${this.canIssue
              ? html`<md-filled-button
                  @click=${() => this.openIssue()}
                  ?disabled=${this.products.length === 0}
                >
                  <span slot="icon">${icon("campaign")}</span>
                  Issue a recall
                </md-filled-button>`
              : null}
          </div>

          <div class="recall-banner">
            ${icon("gpp_maybe")}
            <div>
              <b>Issuing is immediate and public.</b>
              <p>
                A recall is a race against distribution: the moment you issue
                one, it reaches every holder of a covered item at tap time —
                there is no review step and no way to take it back quietly.
                Lifting a notice is public too, and lifted notices stay in the
                history; nothing here is ever deleted.
              </p>
            </div>
          </div>

          ${!this.canIssue
            ? html`<div class="card empty">
                Recall authoring requires the editor or org admin role. Ask an
                organization admin for access.
              </div>`
            : this.loading
              ? html`<div class="center-loading">
                  <md-circular-progress indeterminate></md-circular-progress>
                </div>`
              : this.error
                ? html`<p class="error-text">${this.error}</p>`
                : html`${this.renderActive()} ${this.renderHistory()}`}
        </div>

        ${this.renderIssueDialog()} ${this.renderLiftDialog()}
      </app-shell>
    `;
  }

  private renderIssueDialog() {
    return html`
      <md-dialog id="issue-dialog">
        <div slot="headline">Issue a recall</div>
        <form
          slot="content"
          id="issue-form"
          class="dialog-form"
          @submit=${(e: Event) => void this.handleIssue(e)}
        >
          <md-outlined-select
            label="Product"
            required
            .value=${this.fProduct}
            @change=${(e: Event) => (this.fProduct = inputValue(e))}
          >
            ${this.products.map(
              (p) => html`
                <md-select-option value=${p.ID}>
                  <div slot="headline">
                    ${p.Name}${p.SKU ? ` (${p.SKU})` : ""}
                  </div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
          <md-outlined-select
            label="What should the holder do?"
            .value=${this.fAction}
            @change=${(e: Event) =>
              (this.fAction = inputValue(e) as RecallAction)}
          >
            ${(Object.keys(ACTION_LABELS) as RecallAction[]).map(
              (a) => html`
                <md-select-option value=${a}>
                  <div slot="headline">${ACTION_LABELS[a]}</div>
                </md-select-option>
              `,
            )}
          </md-outlined-select>
          <md-outlined-text-field
            label="Headline"
            required
            supporting-text="The one sentence somebody reads if they read nothing else"
            .value=${this.fHeadline}
            @input=${(e: Event) => (this.fHeadline = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Detail"
            type="textarea"
            rows="3"
            .value=${this.fDetail}
            @input=${(e: Event) => (this.fDetail = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Hazard class"
            placeholder="Class II"
            supporting-text="Your regulator's own classification, verbatim"
            .value=${this.fClass}
            @input=${(e: Event) => (this.fClass = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Regulator reference"
            supporting-text="The official notice number, so anyone can match this to the announcement"
            .value=${this.fReference}
            @input=${(e: Event) => (this.fReference = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Contact URL"
            type="url"
            .value=${this.fContactUrl}
            @input=${(e: Event) => (this.fContactUrl = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Contact phone"
            type="tel"
            supporting-text="Stop-using and return notices need a URL or phone"
            .value=${this.fContactPhone}
            @input=${(e: Event) => (this.fContactPhone = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Batches (comma-separated)"
            supporting-text="Leave empty to recall the whole product"
            .value=${this.fBatches}
            @input=${(e: Event) => (this.fBatches = inputValue(e))}
          ></md-outlined-text-field>
          <md-outlined-text-field
            label="Markets (comma-separated)"
            placeholder="DE, FR"
            supporting-text="Leave empty to recall everywhere"
            .value=${this.fMarkets}
            @input=${(e: Event) => (this.fMarkets = inputValue(e))}
          ></md-outlined-text-field>
          ${this.issueError
            ? html`<p class="error-text">${this.issueError}</p>`
            : null}
        </form>
        <div slot="actions">
          <md-text-button @click=${() => this.issueDialog?.close()}>
            Cancel
          </md-text-button>
          <md-filled-button
            form="issue-form"
            type="submit"
            ?disabled=${this.issuing}
          >
            ${this.issuing ? "Issuing…" : "Issue recall"}
          </md-filled-button>
        </div>
      </md-dialog>
    `;
  }

  private renderLiftDialog() {
    return html`
      <md-dialog id="lift-dialog">
        <div slot="headline">Lift this recall?</div>
        <div slot="content">
          ${this.liftTarget
            ? html`<p>
                <b>${this.productName(this.liftTarget.productId)}</b> ·
                ${this.liftTarget.headline}
              </p>`
            : null}
          <p class="muted">
            Lifting is public: holders who tap a covered item will no longer
            see this notice. The notice itself stays in your history — it is
            never deleted.
          </p>
        </div>
        <div slot="actions">
          <md-text-button @click=${() => this.liftDialog?.close()}>
            Cancel
          </md-text-button>
          <md-filled-button
            ?disabled=${this.lifting}
            @click=${() => void this.handleLift()}
          >
            ${this.lifting ? "Lifting…" : "Lift recall"}
          </md-filled-button>
        </div>
      </md-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "recall-page": RecallPage;
  }
}
